import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ForwardIcon from '@mui/icons-material/Forward';
import { Box, IconButton, Toolbar } from '@mui/material';
import paper from 'paper';

import { FileManager, FileManagerResult } from '@paragon/component-processor';
import { PdfPage, Point2D, TextAnnotation } from '@paragon/contract-library';
import { closeCurrentPopup } from '@paragon/popup-state';
import { getSpinnersToDisplay, SpinnerMessage } from '@paragon/task-spinner';
import { assertExhaustiveSwitch, getModifierKeys } from '@paragon/utilities';

import {
  deleteSelectedItems,
  deselectAllOnActivePage,
  drawLine,
  extractArea,
  moveSelectedItems,
  selectAllWithinRectangle,
  selectItem,
  selectItems,
  toggleItemSelection,
  UpdateInformation,
  updateItemsOnActivePage,
  updatePathWeight,
  whiteoutArea,
} from '../../actions/annotations';
import {
  selectNextDocument,
  selectNextPage,
  selectPreviousDocument,
  selectPreviousPage,
} from '../../actions/documents';
import { addDocuments } from '../../actions/job';
import { showEditAnnotationPrompt } from '../../actions/popup';
import { ReduxDispatch } from '../../actions/thunkActions';
import { fitToPage, resetCanvasCenter, resizeCanvas, setupPaperJs } from '../../modules/annotations/canvasManager';
import { scaleItem, setDialogOpenStatus } from '../../modules/annotations/paperItemEvents';
import {
  getBoundingBoxFromItem,
  getPositionInPaperCoordinates,
  getPositionInPdfCoordinates,
  pixelsToPoints,
  RenderedItem,
} from '../../modules/annotations/paperItemTranslator';
import { LayerEventHandlers } from '../../modules/annotations/paperLayerEvents';
import { renderAnnotationsToPaperJs } from '../../modules/annotations/paperTranslator';
import { panCenter } from '../../modules/SimplePanAndZoom';
import { getAnnotationsForCurrentPageWithSelectionData } from '../../selectors/annotations';
import {
  getSelectedPage,
  getSelectedPageImageInfo,
  getTotalNumberOfPagesInCurrentPdf,
  getTotalNumberOfPdfs,
} from '../../selectors/documents';
import { RootState } from '../../store';
import { setTextAnnotationBeingEditedInlineGuid } from '../../store/annotations';
import EditAnnotation from '../dialogs/annotation-editing/EditAnnotation';
import HelpPanel from '../dialogs/HelpPanel';
import MoreOptionsDialog from '../dialogs/MoreOptions';
import NewAnnotationTemplate from '../dialogs/NewAnnotationTemplate';
import ReorderDocuments from '../dialogs/ReorderDocuments';
import TextInputPrompt from '../dialogs/TextInputPrompt';
import SealJobSpecial from '../SealingTools/SealJobSpecial';
import { EditTextAnnotationInline } from './EditTextAnnotationInline';

declare global {
  interface Window {
    drawingCanvas: DrawingCanvas;
  }
}

interface OwnState {
  readonly textAnnotationBeingEditedTopLeftInCanvasCoordinates: paper.Point | null;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

class DrawingCanvas extends Component<Props, OwnState> {
  drawingCanvas: React.RefObject<HTMLDivElement | null> = React.createRef();
  canvas: React.RefObject<HTMLCanvasElement | null> = React.createRef();

  state: OwnState = {
    textAnnotationBeingEditedTopLeftInCanvasCoordinates: null,
  };

  componentDidMount() {
    window.drawingCanvas = this;

    setupPaperJs(this.canvas.current!, this.getLayerEventHandlers(), this.isZoomEnabled);

    if (this.props.currentPage != null) {
      setupLayerForPage(this.props.currentPage);
      this.renderCurrentPageAnnotations();
    }

    window.onresize = this.resizeCanvas.bind(this);

    this.resizeCanvas();
  }

  componentDidUpdate(prevProps: Props) {
    const firstPageLoaded = this.props.currentPage && !prevProps.currentPage;
    const switchedPages =
      prevProps.currentPage && this.props.currentPage && this.props.currentPage.id !== prevProps.currentPage.id;

    if (firstPageLoaded || switchedPages) {
      setupLayerForPage(this.props.currentPage!);
    }

    if (firstPageLoaded || switchedPages || this.shouldRenderCurrentPageAnnotations(prevProps)) {
      this.renderCurrentPageAnnotations();
    }

    if (this.props.hasPopup !== prevProps.hasPopup) {
      setDialogOpenStatus(this.props.hasPopup);
      document.getElementById('overlay')!.style.display = this.props.hasPopup ? 'block' : 'none';
    }

    this.resizeCanvas();
  }

  shouldRenderCurrentPageAnnotations(prevProps: Props) {
    return (
      this.props.currentPageAnnotations !== prevProps.currentPageAnnotations ||
      this.props.selectedPageImageInfo !== prevProps.selectedPageImageInfo ||
      this.props.userData !== prevProps.userData ||
      this.props.currentUserEmail !== prevProps.currentUserEmail ||
      this.props.userAnnotationsVisible !== prevProps.userAnnotationsVisible ||
      this.props.systemAnnotationsEditable !== prevProps.systemAnnotationsEditable ||
      this.props.textAnnotationBeingEditedInlineGuid !== prevProps.textAnnotationBeingEditedInlineGuid
    );
  }

  renderCurrentPageAnnotations() {
    if (this.props.currentPage == null) {
      return;
    }

    renderAnnotationsToPaperJs(
      this.props.currentPageAnnotations,
      this.props.selectedPageImageInfo!,
      this.props.userData,
      this.props.currentUserEmail!,
      this.props.userAnnotationsVisible,
      this.props.systemAnnotationsEditable,
      this.getItemEventHandlers(),
      this.props.textAnnotationBeingEditedInlineGuid,
    );
  }

  onSingleClick(item: RenderedItem, ctrlKey: boolean) {
    const annotationForItem = this.getAnnotationForItem(item);

    // If exactly one text item is selected, selecting it again enters inline editing mode
    if (this.props.selectedGuids.length === 1 && annotationForItem.type === 'text' && annotationForItem.selected) {
      this.props.setTextAnnotationBeingEditedInlineGuid(annotationForItem.guid);
      this.updateTextAnnotationBeingEditedTopLeftInCanvasCoordinates(annotationForItem.guid);
      return;
    }

    if (ctrlKey) this.props.toggleItemSelection(item.guid);
    else this.props.toggleSelectOnlyThisItem(item.guid);
  }

  onDoubleClick(item: RenderedItem) {
    const annotationForItem = this.getAnnotationForItem(item);

    this.props.showEditAnnotationPrompt({
      annotationBeingEdited: annotationForItem,
      textContent: annotationForItem.type === 'text' ? annotationForItem.contents : null,
      fontSize: (annotationForItem as TextAnnotation).fontSize,
      color: annotationForItem.color,
      annotationType: annotationForItem.type,
      annotationBoundingBox: annotationForItem.boundingBox,
      rotation: annotationForItem.rotation,
      itemBeingEdited: item,
    });
  }

  onDragItem(item: RenderedItem) {
    // If an unselected item is being dragged, it should be immediately selected
    if (!item.reduxSelected) {
      this.props.toggleSelectOnlyThisItem(item.guid);

      // Preemptively set this to true to avoid firing this.props.selectItem more than once (due to timing difference
      //   between DOM events and Redux updates)
      item.reduxSelected = true;
    }
  }

  onDragEnd(item: RenderedItem) {
    this.props.updateItemsOnActivePage([this.getUpdateInformationFromScaling(item)]);
  }

  onItemMouseUp(item: RenderedItem, ctrlKey: boolean) {
    if (ctrlKey) return; // Ctrl key is only used in layer events on mouseup

    // Redux store was not updated while dragging (presumed too many events), update it on mouse up
    const annotationForItem = this.getAnnotationForItem(item);
    const itemPositionInPdfCoordinates = getPositionInPdfCoordinates(
      {
        x: item.position.x,
        y: item.position.y,
      },
      this.props.selectedPageImageInfo!,
    );
    const delta = {
      x: itemPositionInPdfCoordinates.x - annotationForItem.position.x,
      y: itemPositionInPdfCoordinates.y - annotationForItem.position.y,
    };

    this.props.moveSelectedItems(delta);
  }

  isPanEnabled = (event: paper.MouseEvent) => {
    if (this.props.textAnnotationBeingEditedInlineGuid !== null) return false;
    const { ctrlKey, altKey, shiftKey } = getModifierKeys(event.event);
    return !ctrlKey && !altKey && !shiftKey;
  };

  isZoomEnabled = () => this.props.textAnnotationBeingEditedInlineGuid === null;

  onLayerPanDrag = (viewDeltaX: number, viewDeltaY: number) => {
    paper.view.center = panCenter(paper.view.center, viewDeltaX, viewDeltaY, paper.view.zoom);
  };

  updateTextAnnotationBeingEditedTopLeftInCanvasCoordinates = (textAnnotationBeingEditedInlineGuid: string | null) => {
    if (textAnnotationBeingEditedInlineGuid === null) {
      if (this.state.textAnnotationBeingEditedTopLeftInCanvasCoordinates !== null) {
        this.setState({ textAnnotationBeingEditedTopLeftInCanvasCoordinates: null });
      }
      return;
    }

    const pageImageInfo = this.props.selectedPageImageInfo;
    const textAnnotationBeingEdited = this.props.currentPageAnnotations.find(
      (annotation) => annotation.guid === textAnnotationBeingEditedInlineGuid,
    ) as TextAnnotation;

    if (!textAnnotationBeingEdited || pageImageInfo === null) return;

    const width = Math.abs(
      textAnnotationBeingEdited.boundingBox.topRight.x - textAnnotationBeingEdited.boundingBox.topLeft.x,
    );
    const height = Math.abs(
      textAnnotationBeingEdited.boundingBox.topLeft.y - textAnnotationBeingEdited.boundingBox.bottomLeft.y,
    );
    const topLeft: Point2D = {
      x: textAnnotationBeingEdited.position.x - width / 2.0,
      y: textAnnotationBeingEdited.position.y + height / 2.0,
    };
    const topLeftInPaperCoordinates =
      textAnnotationBeingEdited && pageImageInfo
        ? getPositionInPaperCoordinates(topLeft, {
            dpi: pageImageInfo.dpi,
            pageImageHeight: pageImageInfo.pageImageHeight,
            pageImageWidth: pageImageInfo.pageImageWidth,
          })
        : null;
    const topLeftInCanvasCoordinates = topLeftInPaperCoordinates
      ? paper.view.projectToView(topLeftInPaperCoordinates)
      : null;
    this.setState({ textAnnotationBeingEditedTopLeftInCanvasCoordinates: topLeftInCanvasCoordinates });
  };

  onLayerMouseUp = (startDragPoint: paper.Point, endDragPoint: paper.Point, event: paper.MouseEvent) => {
    const { ctrlKey, altKey, shiftKey } = getModifierKeys(event.event);

    if (ctrlKey && shiftKey) {
      // ctrl+shift drag: whiteout area
      this.props.whiteoutArea(startDragPoint, endDragPoint);
    } else if (ctrlKey && altKey) {
      // ctrl+alt drag: select area and whiteout beneath it
      this.props.extractArea(startDragPoint, endDragPoint);
    } else if (ctrlKey) {
      // ctrl drag: select annotations in area
      this.props.selectAllWithinRectangle(startDragPoint, endDragPoint);
    } else if (altKey) {
      this.props.drawLine(startDragPoint, endDragPoint);
    }
  };

  getCurrentPopup() {
    if (!this.props.hasPopup) {
      return null;
    }

    const popup = this.props.currentPopup;
    switch (popup.popupName) {
      case 'NEW_ANNOTATION_TEMPLATE':
        return <NewAnnotationTemplate selectedAnnotations={popup.options.selectedAnnotations} />;

      case 'DOCUMENT_TOOLS':
        return <ReorderDocuments />;

      case 'PLACE_TEXT':
        return <EditAnnotation />;

      case 'EDIT_ANNOTATION':
        return (
          <EditAnnotation
            edit="edit"
            annotationBeingEdited={popup.options.annotationBeingEdited}
            textContent={popup.options.textContent}
            fontSize={popup.options.fontSize}
            color={popup.options.color}
            annotationType={popup.options.annotationType}
            annotationBoundingBox={popup.options.annotationBoundingBox}
            rotation={popup.options.rotation}
            itemBeingEdited={popup.options.itemBeingEdited}
          />
        );

      case 'HELP':
        return <HelpPanel />;

      case 'FILENAME_PROMPT':
        return (
          <TextInputPrompt
            placeholder={`filename${popup.options.format}`}
            submitText={popup.options.action}
            title={popup.options.action}
            onSubmitClick={popup.options.submitClickAction}
          />
        );

      case 'FILE_MANAGER':
        return (
          <FileManager
            componentDesigns={popup.options.componentDesigns}
            miscellaneousFiles={popup.options.miscellaneousFiles}
            requireComponentsOrOtherPdfs={false}
            showOther
            onSubmit={(result) => this.props.onAddDocuments(result)}
            onCancel={() => this.props.closeCurrentPopup()}
          />
        );

      case 'MORE_OPTIONS':
        return <MoreOptionsDialog />;

      case 'SEAL_JOB_SPECIAL':
        return <SealJobSpecial />;

      default:
        return null;
    }
  }

  getAnnotationForItem(item: RenderedItem) {
    return this.props.currentPageAnnotations.find((annotation) => annotation.guid === item.guid)!;
  }

  getUpdateInformationFromScaling(item: RenderedItem) {
    const pageImageInfo = this.props.selectedPageImageInfo!;

    // Each item has different properties adjusted when it scales, so update the appropriate
    //   properties based on the type of object
    const annotationForItem = this.getAnnotationForItem(item);
    const itemPositionInPdfCoordinates = getPositionInPdfCoordinates(
      {
        x: item.position.x,
        y: item.position.y,
      },
      pageImageInfo,
    );
    const updatedBoundingBox = getBoundingBoxFromItem(item, pageImageInfo);
    const baseUpdatedProperties = {
      position: {
        x: itemPositionInPdfCoordinates.x,
        y: itemPositionInPdfCoordinates.y,
      },
      boundingBox: updatedBoundingBox,
    };

    const { pageImageDpi } = this.props.currentPage!;
    const itemScaling = item.scaling;

    if (annotationForItem.type === 'text') {
      return {
        guid: annotationForItem.guid,
        updatedProperties: {
          ...baseUpdatedProperties,
          fontSize: pixelsToPoints((item as unknown as paper.PointText).fontSize as number, pageImageDpi),
          leading: pixelsToPoints((item as unknown as paper.PointText).leading, pageImageDpi),
        },
      };
    }
    if (annotationForItem.type === 'image') {
      return {
        guid: annotationForItem.guid,
        updatedProperties: {
          ...baseUpdatedProperties,
          scale: {
            x: itemScaling.x,
            y: itemScaling.y,
          },
        },
      };
    }
    if (annotationForItem.type === 'shape') {
      return {
        guid: annotationForItem.guid,
        updatedProperties: {
          ...baseUpdatedProperties,
          width: pixelsToPoints((item as unknown as paper.Shape.Rectangle).size.width * itemScaling.x, pageImageDpi),
          height: pixelsToPoints((item as unknown as paper.Shape.Rectangle).size.height * itemScaling.y, pageImageDpi),
        },
      };
    }
    if (annotationForItem.type === 'path') {
      return {
        guid: annotationForItem.guid,
        updatedProperties: {
          ...baseUpdatedProperties,
          segments: (item as unknown as paper.Path).segments.map((segment) =>
            getPositionInPdfCoordinates(segment.point, pageImageInfo),
          ),
        },
      };
    }

    assertExhaustiveSwitch(annotationForItem, 'Expected to cover all annotation types');
  }

  getLayerEventHandlers = (): LayerEventHandlers => ({
    isPanEnabled: this.isPanEnabled,

    onClick: this.props.deselectAllOnActivePage,
    onMouseUp: this.onLayerMouseUp,
    onPanDrag: this.onLayerPanDrag,
  });

  getItemEventHandlers() {
    return {
      singleClick: this.onSingleClick.bind(this),
      doubleClick: this.onDoubleClick.bind(this),
      dragItem: this.onDragItem.bind(this),
      dragEnd: this.onDragEnd.bind(this),
      mouseUp: this.onItemMouseUp.bind(this),
    };
  }

  resizeCanvas() {
    const widthHasChanged = this.canvas.current!.width !== this.drawingCanvas.current!.clientWidth;
    const heightHasChanged = this.canvas.current!.height !== this.drawingCanvas.current!.clientHeight;

    if (widthHasChanged || heightHasChanged) {
      this.canvas.current!.width = this.drawingCanvas.current!.clientWidth;
      this.canvas.current!.height = this.drawingCanvas.current!.clientHeight;
      resizeCanvas();
    }
  }

  scaleUpTenPercent(event: KeyboardEvent) {
    const delta = 1.1;

    const updateInformation = (paper.project.activeLayer.children as unknown as readonly RenderedItem[]).reduce<
      UpdateInformation[]
    >((accumulator, item) => {
      if (item.reduxSelected) {
        if (item instanceof paper.Path && event.altKey) {
          this.props.updatePathWeight(item.guid, delta);
        } else {
          scaleItem(item, delta);
          accumulator.push(this.getUpdateInformationFromScaling(item));
        }
      }
      return accumulator;
    }, []);

    if (updateInformation.length > 0) {
      this.props.updateItemsOnActivePage(updateInformation);
    }
  }

  scaleDownTenPercent(event: KeyboardEvent) {
    const delta = 0.9;

    const updateInformation = (paper.project.activeLayer.children as unknown as readonly RenderedItem[]).reduce<
      UpdateInformation[]
    >((accumulator, item) => {
      if (item.reduxSelected) {
        if (item instanceof paper.Path && event.altKey) {
          this.props.updatePathWeight(item.guid, delta);
        } else {
          scaleItem(item, delta);
          accumulator.push(this.getUpdateInformationFromScaling(item));
        }
      }
      return accumulator;
    }, []);

    if (updateInformation.length > 0) {
      this.props.updateItemsOnActivePage(updateInformation);
    }
  }

  render() {
    const navTools = [];
    if (this.props.activePdfNumberOfPages > 1) {
      navTools.push(
        <IconButton
          id="previousPageArrow"
          sx={{
            position: 'absolute',
            left: 4,
            top: '40%',
            color: 'navArrows.main',
            '&:hover': {
              color: 'primary.main',
            },
          }}
          onClick={this.props.selectPreviousPage}
          key="previousPage"
        >
          <ArrowBackIcon />
        </IconButton>,
      );

      navTools.push(
        <IconButton
          id="nextPageArrow"
          sx={{
            position: 'absolute',
            right: 4,
            top: '40%',
            color: 'navArrows.main',
            '&:hover': {
              color: 'primary.main',
            },
          }}
          onClick={this.props.selectNextPage}
          key="nextPage"
        >
          <ArrowForwardIcon />
        </IconButton>,
      );
    }
    if (this.props.totalNumberPdfs > 1) {
      navTools.push(
        <Box sx={{ position: 'absolute', width: '100%', bottom: '100%' }} key="previousFile">
          <IconButton
            id="previousDocumentArrow"
            sx={{
              top: 54,
              left: '45%',
              transform: 'rotate(-90deg)',
              color: 'navArrows.main',
              '&:hover': {
                color: 'primary.main',
              },
            }}
            onClick={this.props.selectPreviousDocument}
          >
            <ForwardIcon />
          </IconButton>
        </Box>,
      );
      navTools.push(
        <Box sx={{ position: 'absolute', width: '100%', top: '100%' }} key="nextFile">
          <IconButton
            id="nextDocumentArrow"
            sx={{
              bottom: 54,
              left: '45%',
              transform: 'rotate(90deg)',
              color: 'navArrows.main',
              '&:hover': {
                color: 'primary.main',
              },
            }}
            onClick={this.props.selectNextDocument}
          >
            <ForwardIcon />
          </IconButton>
        </Box>,
      );
    }

    const messages = Array.from(this.props.spinnerMessages).map((text) => <SpinnerMessage text={text} key={text} />);

    return (
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minWidth: 0, // So the Typography noWrap works
          bgcolor: 'background.default',
          mr: -50,
        }}
      >
        <Toolbar />
        {messages}
        <Box sx={{ flex: '1 0', position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              flex: '3 1 auto',
              height: '100%',
              overflow: 'visible',
              display: 'flex',
              bgcolor: 'canvas.main',
            }}
            id="drawingCanvas"
            ref={this.drawingCanvas}
          >
            <Box
              component="canvas"
              sx={{ position: 'absolute', flex: '0 0' }}
              ref={this.canvas}
              data-paper-resize="true"
            />
            {this.props.textAnnotationBeingEditedInlineGuid &&
              this.state.textAnnotationBeingEditedTopLeftInCanvasCoordinates !== null && (
                <EditTextAnnotationInline
                  textAnnotationBeingEditedInlineGuid={this.props.textAnnotationBeingEditedInlineGuid}
                  topLeftInCanvasCoordinates={this.state.textAnnotationBeingEditedTopLeftInCanvasCoordinates}
                />
              )}
            {navTools}
            {this.getCurrentPopup()}
          </Box>
        </Box>
      </Box>
    );
  }
}

function setupLayerForPage(currentPage: PdfPage) {
  paper.project.activeLayer.removeChildren();
  resetCanvasCenter();

  const backgroundImageRaster = new paper.Raster({
    crossOrigin: 'anonymous',
    source: currentPage.url,
    position: paper.view.center,
  });

  backgroundImageRaster.onLoad = function onBackgroundRasterLoad() {
    if (backgroundImageRaster.width === 0 || backgroundImageRaster.height === 0) {
      // FIXME: what is causing this?
      console.log('Page image failed to load. The cause of this bug is currently unknown');
      return;
    }

    fitToPage(backgroundImageRaster.width, backgroundImageRaster.height);
  };
}

function mapStateToProps(state: RootState) {
  let currentUserEmail = null;
  if (state.logInData.loggedInUser != null) currentUserEmail = state.logInData.loggedInUser.email;
  return {
    spinnerMessages: getSpinnersToDisplay(state),
    hasPopup: state.popup.popupName != null,
    currentPopup: state.popup,
    currentPage: getSelectedPage(state),
    currentPageAnnotations: getAnnotationsForCurrentPageWithSelectionData(state),
    totalNumberPdfs: getTotalNumberOfPdfs(state),
    activePdfNumberOfPages: getTotalNumberOfPagesInCurrentPdf(state),
    currentUserEmail,
    userData: state.users.userData,
    userAnnotationsVisible: state.view.userAnnotationsVisible,
    selectedPageImageInfo: getSelectedPageImageInfo(state),
    systemAnnotationsEditable: state.annotations.systemAnnotationsEditable,
    textAnnotationBeingEditedInlineGuid: state.annotations.textAnnotationBeingEditedInlineGuid,
    selectedGuids: state.annotations.selectedGuids,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch) {
  const boundActions = bindActionCreators(
    {
      selectPreviousDocument,
      selectNextDocument,
      selectPreviousPage,
      selectNextPage,
      moveSelectedItems,
      selectItem,
      selectItems,
      toggleItemSelection,
      showEditAnnotationPrompt,
      deleteSelectedItems,
      deselectAllOnActivePage,
      closeCurrentPopup,
      updateItemsOnActivePage,
      selectAllWithinRectangle,
      whiteoutArea,
      extractArea,
      drawLine,
      updatePathWeight,
      setTextAnnotationBeingEditedInlineGuid,
    },
    dispatch,
  );

  return {
    ...boundActions,

    toggleSelectOnlyThisItem: (guid: string) => dispatch(toggleItemSelection(guid, true)),
    onAddDocuments: (result: FileManagerResult) => {
      dispatch(closeCurrentPopup());
      dispatch(addDocuments(result));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DrawingCanvas);
